import { Box, Typography, useTheme, Select, MenuItem, FormControl, Paper, ListSubheader } from "@mui/material";
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { toPng } from "html-to-image";
import { useEffect, useMemo, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { getBarOptions } from "../components/chart/BarChartWithTabs";
import { getColors } from "../components/chart/DoughnutChart";
import DonutChart from "../components/chart/DonutChart";
import { scope1Categories, scope2Categories, scope3Categories } from "../pages/Organization/ScopeCategory";
import { FiArrowUp, FiArrowDown } from "react-icons/fi";
import ScopeSummaryTable from "./ScopeSummaryTable";
import { getOrgTokens, ORG_PALETTE } from "../theme/style/organizationPalette";
import { GRAPH_PALETTE } from "../theme/style/graphChartPalette";
import { TableHeaderContainer } from "../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../components/container/TableTitleContainer";
import { useContextProvider } from "../components/page-context/ContextProvider";
import { findEmissionOptionMatches } from "../components/page-context/Helpers/buildOptions";
import { Chip } from "../components/m3";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function ScopeFootprint({ emissions, emissionDetails }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const barOptions = getBarOptions(isDark);
  const all = useMemo(() => ({ label: "All", value: "All" }), []);
  const scope1 = useMemo(() => ({ label: "Scope 1", value: "Scope 1" }), []);
  const scope2 = useMemo(() => ({ label: "Scope 2", value: "Scope 2" }), []);
  const scope3 = useMemo(() => ({ label: "Scope 3", value: "Scope 3" }), []);
  const scope4 = useMemo(() => ({ label: "Avoided", value: "Scope4" }), []);
  const scope5 = useMemo(() => ({ label: "Biogenic", value: "Scope5" }), []);
  const scopes = useMemo(() => [all, scope1, scope2, scope3, scope4, scope5], [all, scope1, scope2, scope3, scope4, scope5]);
  const [selectedScope, setSelectedScope] = useState(all.value);
  const [unit, setUnit] = useState("kg");
  const footprintBarChartRef = useRef(null);

  // Aggressive data cleanup: remove nulls, NaNs, and empty categories
  const validEmissions = useMemo(() => {
    return (emissions || []).filter(e => {
      if (!e || typeof e !== 'object' || !e.category) return false;
      const gwp = Number(e.gwp) || Number(e.gwp_total) || 0;
      const biogenic_gwp = Number(e.biogenic_gwp) || 0;
      if (!isFinite(gwp) && !isFinite(biogenic_gwp)) return false;
      // Must have a category and a valid value/flag
      return e.category.trim().length > 0 && (gwp > 0 || biogenic_gwp > 0 || e.is_avoided === 1 || e.is_avoided === true || e.is_biogenic === 1 || e.is_biogenic === true);
    });
  }, [emissions]);

  // ── Date Availability & Filter Extraction ─────────────────────────
  const hasDateData = useMemo(() => {
    return (emissions || []).some(
      (e) => Boolean(e.billing_period_start || e.billing_period_end || e.period_type)
    );
  }, [emissions]);

  const [selectedPeriodKey, setSelectedPeriodKey] = useState("all");
  const [selectedPeriodType, setSelectedPeriodType] = useState("all"); // "all", "annual", "quarterly", "monthly"
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedSubPeriod, setSelectedSubPeriod] = useState("all"); // "Q1"..."Q4" or month name

  const handlePeriodKeyChange = (event) => {
    const val = event.target.value;
    setSelectedPeriodKey(val);
    if (val === "all") {
      setSelectedPeriodType("all");
      setSelectedSubPeriod("all");
      setSelectedYear("all");
    } else if (val.startsWith("annual")) {
      const parts = val.split(":");
      setSelectedPeriodType("annual");
      setSelectedSubPeriod("all");
      setSelectedYear(parts[1] || "all");
    } else if (val.startsWith("quarter:")) {
      const q = val.split(":")[1];
      setSelectedPeriodType("quarterly");
      setSelectedSubPeriod(q);
      setSelectedYear("all");
    } else if (val.startsWith("month:")) {
      const m = val.split(":")[1];
      setSelectedPeriodType("monthly");
      setSelectedSubPeriod(m);
      setSelectedYear("all");
    }
  };

  const availableYears = useMemo(() => {
    if (!hasDateData) return [];
    const years = new Set();
    (emissions || []).forEach((e) => {
      [e.billing_period_start, e.billing_period_end].forEach((d) => {
        if (d && typeof d === "string") {
          const m = d.match(/\b(20\d\d)\b/);
          if (m) years.add(m[1]);
        }
      });
    });
    (emissionDetails || []).forEach((d) => {
      if (d.description) {
        const m = d.description.match(/\b(20\d\d)\b/);
        if (m) years.add(m[1]);
      }
    });
    return Array.from(years).sort().reverse();
  }, [hasDateData, emissions, emissionDetails]);


  const availableQuarters = useMemo(() => ["Q1", "Q2", "Q3", "Q4"], []);
  const availableMonths = useMemo(() => [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ], []);

  const monthToQuarter = useMemo(() => ({
    january: "Q1", february: "Q1", march: "Q1",
    april: "Q2", may: "Q2", june: "Q2",
    july: "Q3", august: "Q3", september: "Q3",
    october: "Q4", november: "Q4", december: "Q4"
  }), []);

  const getQuarterFromDateStr = (dateStr) => {
    if (!dateStr) return null;
    try {
      const parts = String(dateStr).split("-");
      if (parts.length >= 2) {
        const monthNum = parseInt(parts[1], 10);
        if (monthNum >= 1 && monthNum <= 3) return "Q1";
        if (monthNum >= 4 && monthNum <= 6) return "Q2";
        if (monthNum >= 7 && monthNum <= 9) return "Q3";
        if (monthNum >= 10 && monthNum <= 12) return "Q4";
      }
    } catch (_) {}
    return null;
  };

  // Filtered child details based on active date selections
  const dateFilteredDetails = useMemo(() => {
    if (!hasDateData || (selectedPeriodType === "all" && selectedYear === "all" && selectedSubPeriod === "all")) {
      return emissionDetails || [];
    }

    return (emissionDetails || []).filter((d) => {
      const descLower = (d.description || "").toLowerCase();
      const parent = (emissions || []).find((e) => e.id === d.emission_id);

      if (selectedYear !== "all") {
        const hasYearInDesc = descLower.includes(selectedYear);
        const hasYearInParent = parent && (
          (parent.billing_period_start && parent.billing_period_start.includes(selectedYear)) ||
          (parent.billing_period_end && parent.billing_period_end.includes(selectedYear))
        );
        if (!hasYearInDesc && !hasYearInParent) return false;
      }

      if (selectedPeriodType === "quarterly" && selectedSubPeriod !== "all") {
        const targetQ = selectedSubPeriod.toLowerCase();
        const matchesQ = descLower.includes(targetQ);
        const matchesM = Object.entries(monthToQuarter).some(
          ([mName, q]) => q.toLowerCase() === targetQ && descLower.includes(mName)
        );
        const pQ = parent ? getQuarterFromDateStr(parent.billing_period_start) : null;
        if (!matchesQ && !matchesM && (!pQ || pQ.toLowerCase() !== targetQ)) {
          return false;
        }
      } else if (selectedPeriodType === "monthly" && selectedSubPeriod !== "all") {
        const targetM = selectedSubPeriod.toLowerCase();
        const matchesDesc = descLower.includes(targetM);
        let matchesParent = false;
        if (parent?.billing_period_start) {
          const parts = String(parent.billing_period_start).split("-");
          if (parts.length >= 2) {
            const mIdx = parseInt(parts[1], 10) - 1;
            if (mIdx >= 0 && mIdx < availableMonths.length) {
              matchesParent = availableMonths[mIdx].toLowerCase() === targetM;
            }
          }
        }
        if (!matchesDesc && !matchesParent) return false;
      }

      return true;
    });
  }, [hasDateData, selectedPeriodType, selectedYear, selectedSubPeriod, emissionDetails, emissions, monthToQuarter, availableMonths]);

  // Filtered parent emissions with recalculated GWP when a sub-period slice is selected
  const dateFilteredEmissions = useMemo(() => {
    if (!hasDateData || (selectedPeriodType === "all" && selectedYear === "all" && selectedSubPeriod === "all")) {
      return validEmissions;
    }

    const filtered = [];
    validEmissions.forEach((e) => {
      const parentStart = e.billing_period_start || "";
      const parentEnd = e.billing_period_end || "";

      if (selectedYear !== "all") {
        const matchesStart = parentStart.includes(selectedYear);
        const matchesEnd = parentEnd.includes(selectedYear);
        const hasDetailsInYear = dateFilteredDetails.some((d) => d.emission_id === e.id);
        if (!matchesStart && !matchesEnd && !hasDetailsInYear) return;
      }

      const matchingDetails = dateFilteredDetails.filter((d) => d.emission_id === e.id);
      if (matchingDetails.length > 0) {
        const periodGwp = matchingDetails.reduce((sum, d) => sum + (Number(d.totalco2eq) || 0), 0);
        const periodBiogenic = matchingDetails.reduce((sum, d) => sum + (Number(d.biogenic_total) || 0), 0);
        filtered.push({
          ...e,
          gwp: periodGwp,
          biogenic_gwp: periodBiogenic,
        });
      } else if (selectedSubPeriod === "all") {
        filtered.push(e);
      } else {
        if (selectedPeriodType === "quarterly") {
          const pQ = getQuarterFromDateStr(parentStart);
          if (pQ && pQ.toLowerCase() === selectedSubPeriod.toLowerCase()) {
            filtered.push(e);
          }
        } else if (selectedPeriodType === "monthly") {
          const parts = String(parentStart).split("-");
          if (parts.length >= 2) {
            const mIdx = parseInt(parts[1], 10) - 1;
            if (mIdx >= 0 && mIdx < availableMonths.length && availableMonths[mIdx].toLowerCase() === selectedSubPeriod.toLowerCase()) {
              filtered.push(e);
            }
          }
        }
      }
    });

    return filtered;
  }, [hasDateData, selectedPeriodType, selectedYear, selectedSubPeriod, validEmissions, dateFilteredDetails, availableMonths]);

  const emissionGraph = useMemo(() => ({ label: "Emission Graph", value: "Emission Graph" }), []);
  const detailsGraph = useMemo(() => ({ label: "Details Graph", value: "Details Graph" }), []);
  const graphs = useMemo(() => [emissionGraph, detailsGraph], [emissionGraph, detailsGraph]);
  const [selectedGraph, setSelectedGraph] = useState(emissionGraph.value);
  const [emissionDetailsBarData, setEmissionDetailsBarData] = useState({
    labels: [],
    datasets: [],
  });
  const scopedEmissions = useMemo(() => {
    if (!dateFilteredEmissions || dateFilteredEmissions.length < 1) return [];
    switch (selectedScope) {
      case all.value:
        return dateFilteredEmissions;
      case scope1.value:
        return dateFilteredEmissions.filter((item) => !item.is_avoided && scope1Categories.includes(item.category));
      case scope2.value:
        return dateFilteredEmissions.filter((item) => !item.is_avoided && scope2Categories.includes(item.category));
      case scope3.value:
        return dateFilteredEmissions.filter((item) => !item.is_avoided && scope3Categories.includes(item.category));
      case scope4.value:
        return dateFilteredEmissions.filter((item) => item.is_avoided);
      case scope5.value:
        return dateFilteredEmissions.filter((item) => item.is_biogenic);
      default:
        return [];
    }
  }, [dateFilteredEmissions, selectedScope, all, scope1, scope2, scope3, scope4, scope5]);
  const handleLCAStageChange = (event) => {
    setSelectedScope(event.target.value);
  };

  const scopedEmissionDetails = useMemo(() => {
    if (!scopedEmissions || scopedEmissions.length === 0) return [];
    const validEmissionIds = new Set(scopedEmissions.map(e => e.id));
    return dateFilteredDetails.filter(detail => validEmissionIds.has(detail.emission_id));
  }, [scopedEmissions, dateFilteredDetails]);

  const [emissionBarData, setEmissionBarData] = useState({
    labels: [],
    datasets: [],
  });
  useEffect(() => {
    const getScopeColorForCategory = (cat) => {
      if (cat.includes("(Avoided)")) return "#22c55e";
      if (cat.includes("(Biogenic)")) return "#8b5cf6";
      if (scope1Categories.includes(cat)) return "#ef4444";
      if (scope2Categories.includes(cat)) return "#f59e0b";
      if (scope3Categories.includes(cat)) return "#0ea5e9";
      return "#64748b";
    };

    const catMap = {};
    const m = unit === "t" ? 0.001 : 1;
    scopedEmissions.forEach(item => {
      let label = item.category;
      if (item.is_avoided) label += " (Avoided)";
      else if (item.is_biogenic) label += " (Biogenic)";

      if (!catMap[label]) catMap[label] = 0;
      const gwpVal = Number(item.gwp) || Number(item.gwp_total) || Number(item.biogenic_gwp) || 0;
      catMap[label] += (gwpVal * m);
    });

    const sortedEntries = Object.entries(catMap).sort(([labelA], [labelB]) => {
      const numA = parseInt(labelA) || 9999;
      const numB = parseInt(labelB) || 9999;
      return numA !== numB ? numA - numB : labelA.localeCompare(labelB);
    });

    const labels = sortedEntries.map(([label]) => label);
    const data = sortedEntries.map(([, v]) => Math.abs(v));

    const backgroundColor = labels.map(label => getScopeColorForCategory(label));
    const borderColor = backgroundColor;

    setEmissionBarData({
      labels,
      datasets: [
        {
          label: "GWP",
          data,
          backgroundColor,
          borderColor,
          borderWidth: 1.5,
          borderRadius: 8,
          pointRadius: 3,
          pointHoverRadius: 3,
          pointBackgroundColor: backgroundColor,
          tension: 0.05,
        },
      ],
    });
  }, [scopedEmissions, unit]);

  useEffect(() => {
    const descMap = {};
    const descColorMap = {};
    const m = unit === "t" ? 0.001 : 1;

    const getScopeColorForCategory = (cat) => {
      if (cat.includes("(Avoided)")) return "#22c55e"; // Green
      if (cat.includes("(Biogenic)")) return "#8b5cf6"; // Purple
      if (scope1Categories.includes(cat)) return "#ef4444"; // Red
      if (scope2Categories.includes(cat)) return "#f59e0b"; // Orange
      if (scope3Categories.includes(cat)) return "#0ea5e9"; // Blue
      return "#64748b"; // Fallback
    };

    scopedEmissionDetails.forEach((item) => {
      const desc = item.description;
      if (!descMap[desc]) {
        descMap[desc] = 0;
        const emission = scopedEmissions.find(e => e.id === item.emission_id);
        const cat = emission ? (emission.is_avoided ? emission.category + " (Avoided)" : emission.is_biogenic ? emission.category + " (Biogenic)" : emission.category) : "";
        descColorMap[desc] = getScopeColorForCategory(cat);
      }
      descMap[desc] += (item.totalco2eq * m);
    });

    const labels = Object.keys(descMap);
    const data = Object.values(descMap);

    const backgroundColor = labels.map(desc => descColorMap[desc] || "#64748b");
    const borderColor = backgroundColor;

    setEmissionDetailsBarData({
      labels,
      datasets: [
        {
          label: "CO2 Emission",
          data: data.map(v => Math.abs(v)), // Use absolute value for consistency
          backgroundColor,
          borderColor,
          borderWidth: 1.5,
          borderRadius: 8,
          pointRadius: 3,
          pointHoverRadius: 3,
          pointBackgroundColor: backgroundColor,
          tension: 0.05,
        },
      ],
    });
  }, [scopedEmissionDetails, scopedEmissions, unit]);

  const totalGWPs = useMemo(() => {
    let scope1 = 0;
    let scope2 = 0;
    let scope3 = 0;
    let scope4 = 0;
    let scope5 = 0;
    dateFilteredEmissions.forEach((item) => {
      const gwpVal = Number(item.gwp) || Number(item.gwp_total) || Number(item.biogenic_gwp) || 0;
      if (!item.is_avoided && !item.is_biogenic && scope1Categories.includes(item.category)) {
        scope1 += gwpVal;
      }
      if (!item.is_avoided && !item.is_biogenic && scope2Categories.includes(item.category)) {
        scope2 += gwpVal;
      }
      if (!item.is_avoided && !item.is_biogenic && scope3Categories.includes(item.category)) {
        scope3 += gwpVal;
      }
      if (item.is_avoided) {
        scope4 += gwpVal;
      }
      if (item.is_biogenic) {
        scope5 += gwpVal;
      }
    });

    const m = unit === "t" ? 0.001 : 1;
    return {
      scope1: scope1 * m,
      scope2: scope2 * m,
      scope3: scope3 * m,
      scope4: scope4 * m,
      scope5: scope5 * m,
    };
  }, [dateFilteredEmissions, unit]);

  // Gross total for percentage calculation (S1+S2+S3 only)
  const grossTotal = useMemo(() => {
    return (totalGWPs.scope1 || 0) + (totalGWPs.scope2 || 0) + (totalGWPs.scope3 || 0);
  }, [totalGWPs]);

  const [doughnutData, setDoughnutData] = useState();
  useEffect(() => {
    const backgroundColor = [];
    const borderColor = [];
    const labels = selectedScope === "All" ? ["Scope 1", "Scope 2", "Scope 3"] : scopedEmissions.map((item) => item.category);
    labels.forEach((_, index) => {
      const color = getColors(index);
      backgroundColor.push(color);
      borderColor.push(color);
    });
    const data = selectedScope === "All" ? [totalGWPs.scope1, totalGWPs.scope2, totalGWPs.scope3] : scopedEmissions.map((item) => item.gwp);
    setDoughnutData({ labels, datasets: [{ data, backgroundColor, borderColor, borderWidth: 1 }] });
  }, [scopedEmissions, selectedScope, totalGWPs]);

  const downloadFootprintBarChart = () => {
    if (footprintBarChartRef.current) {
      toPng(footprintBarChartRef.current)
        .then((dataUrl) => {
          const link = document.createElement("a");
          link.href = dataUrl;
          link.download = "environmental_footprint.png";
          link.click();
        })
        .catch((error) => {
          console.error("Error generating image:", error);
        });
    }
  };

  const handleGraphChange = (e) => {
    setSelectedGraph(e.target.value);
  };

  const donutDataNew = [
    { label: "Scope 1", value: totalGWPs.scope1, color: "#ef4444" },
    { label: "Scope 2", value: totalGWPs.scope2, color: "#f59e0b" },
    { label: "Scope 3", value: totalGWPs.scope3, color: "#0ea5e9" },
  ];

  if (Math.abs(totalGWPs.scope4) > 0) {
    donutDataNew.push({ label: "Avoided", value: totalGWPs.scope4, color: "#22c55e" });
  }

  if (Math.abs(totalGWPs.scope5) > 0) {
    donutDataNew.push({ label: "Biogenic", value: totalGWPs.scope5, color: "#8b5cf6" });
  }

  const totalPortfolio = donutDataNew.reduce((sum, item) => sum + Math.abs(item.value), 0);

  // Detailed Scope Summary table data
  const currentTotal = totalPortfolio;

  const maxCategory = dateFilteredEmissions.reduce((max, item) => {
    const gwpVal = Number(item.gwp) || Number(item.gwp_total) || Number(item.biogenic_gwp) || 0;
    return gwpVal > max.gwp ? { category: item.category, gwp: gwpVal } : max;
  }, { category: "N/A", gwp: 0 });

  const categoryToScope = (cat) => {
    if (scope1Categories.includes(cat)) return "Scope 1";
    if (scope2Categories.includes(cat)) return "Scope 2";
    if (scope3Categories.includes(cat)) return "Scope 3";
    return "Uncategorized";
  };

  const { emissionDetailOptions } = useContextProvider();

  const tableData = useMemo(() => {
    let categoryMap = {};
    const m = unit === "t" ? 0.001 : 1;
    const unitLabel = unit === "t" ? "t" : "kg";

    const pollutantMeta = [
      { key: "co2", label: "CO₂", unit: unitLabel, color: "#3b82f6" },
      { key: "ch4", label: "CH₄", unit: unitLabel, color: "#eab308" },
      { key: "n2o", label: "N₂O", unit: unitLabel, color: "#a855f7" },
      { key: "nox", label: "NOx", unit: unitLabel, color: "#ef4444" },
      { key: "pm25", label: "PM2.5", unit: unitLabel, color: "#f97316" },
      { key: "pm10", label: "PM10", unit: unitLabel, color: "#06b6d4" },
      { key: "black_carbon", label: "Black Carbon", unit: unitLabel, color: "#64748b" },
    ];

    dateFilteredEmissions.forEach((item) => {
      const cat = item.category || "Unspecified";
      let scope = categoryToScope(cat);
      if (item.is_avoided) {
        scope = "Avoided";
      } else if (item.is_biogenic) {
        scope = "Biogenic";
      }

      const key = `${scope}-${cat}`;
      if (!categoryMap[key]) categoryMap[key] = { scope, category: cat, gwp: 0 };
      const gwpVal = Number(item.gwp) || Number(item.gwp_total) || Number(item.biogenic_gwp) || 0;
      categoryMap[key].gwp += gwpVal * m;
    });

    // Sort so groupings are contiguous: Scope 1 -> Scope 2 -> Scope 3 -> Avoided -> Biogenic
    const scopeOrder = { "Scope 1": 1, "Scope 2": 2, "Scope 3": 3, "Avoided": 4, "Biogenic": 5, "Uncategorized": 6 };
    const sorted = Object.values(categoryMap).sort((a, b) => (scopeOrder[a.scope] ?? 9) - (scopeOrder[b.scope] ?? 9) || b.gwp - a.gwp);

    let scopeCounts = {};
    sorted.forEach(row => {
      scopeCounts[row.scope] = (scopeCounts[row.scope] || 0) + 1;
    });

    let scopeTracker = {};
    return sorted
      .filter(row => {
        const hasScope = row.scope && row.scope !== "Uncategorized";
        const hasCategory = row.category && row.category !== "Unspecified";
        const hasValue = row.gwp > 0 || row.scope === "Avoided" || row.scope === "Biogenic" || Math.abs(row.gwp) > 0;
        return hasScope && hasCategory && hasValue;
      })
      .map(row => {
        const isFirst = !scopeTracker[row.scope];
        scopeTracker[row.scope] = true;

        // Calculate pollutant breakdown for this specific category
        const matchingEmissionIds = new Set(
          (dateFilteredEmissions || [])
            .filter((e) => e.category === row.category)
            .map((e) => e.id)
        );

        const catTotals = { co2: 0, ch4: 0, n2o: 0, nox: 0, pm25: 0, pm10: 0, black_carbon: 0 };
        const catHasVal = { co2: false, ch4: false, n2o: false, nox: false, pm25: false, pm10: false, black_carbon: false };

        (dateFilteredDetails || []).forEach((ed) => {
          if (!matchingEmissionIds.has(ed.emission_id)) return;
          const { type, category, sub_category, description, quantity, factor, uncertainty } = ed;

          const qty = parseFloat(quantity) || 0;
          const f = (factor !== null && factor !== undefined && factor !== "") ? parseFloat(factor) : 1.0;
          const u = (uncertainty !== null && uncertainty !== undefined && uncertainty !== "") ? parseFloat(uncertainty) : 1.0;
          const multiplier = qty * f * u;

          const matches = findEmissionOptionMatches(emissionDetailOptions, type, category, sub_category, description);
          const match = (ed.unit ? matches.find((m) => m.unit?.toLowerCase().trim() === ed.unit?.toLowerCase().trim()) : null) || matches[0];

          ["co2", "ch4", "n2o", "nox", "pm25", "pm10", "black_carbon"].forEach((p) => {
            let val = null;
            if (ed[`total_${p}`] !== undefined && ed[`total_${p}`] !== null && !isNaN(ed[`total_${p}`]) && ed[`total_${p}`] > 0) {
              val = ed[`total_${p}`];
            } else if (ed[`${p}_factor`] !== undefined && ed[`${p}_factor`] !== null && !isNaN(ed[`${p}_factor`]) && ed[`${p}_factor`] > 0) {
              val = multiplier * ed[`${p}_factor`];
            } else if (match && match[p] !== null && match[p] !== undefined && match[p] !== "") {
              const parsed = parseFloat(match[p]);
              if (!isNaN(parsed) && parsed > 0) {
                val = multiplier * parsed;
              }
            }

            if (val !== null && val > 0) {
              catTotals[p] += val;
              catHasVal[p] = true;
            }
          });
        });

        const catPollutants = pollutantMeta
          .filter((p) => catHasVal[p.key] && catTotals[p.key] > 0)
          .map((p) => ({
            key: p.key,
            label: p.label,
            unit: p.unit,
            color: p.color,
            value: catTotals[p.key] * m,
          }));

        return {
          id: `${row.scope}-${row.category}`.replace(/\s+/g, '-'),
          scope: row.scope,
          category: row.category,
          gwp: row.gwp,
          pollutants: catPollutants,
          isFirstInScope: isFirst,
          scopeSpan: scopeCounts[row.scope]
        };
      });
  }, [dateFilteredEmissions, dateFilteredDetails, emissionDetailOptions, unit]);

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, alignItems: "stretch", animation: "org-fadeInUp 0.4s ease" }}>

        {/* Doughnut Chart Section */}
        <Box sx={{ flex: "1 1 25%", minWidth: 350, order: { xs: 2, md: 2 } }}>
          <Box sx={{ p: ORG_PALETTE.spacing.chartPad, height: "100%", borderRadius: ORG_PALETTE.radius.card, bgcolor: tok.bg.card, border: `1px solid ${tok.border.card}` }}>
            <Typography variant="h6" sx={{ mb: 4, pb: 2, fontWeight: 500, color: tok.text.chartTitle, borderBottom: `1px solid ${tok.border.divider}` }}>{t("Scopes Summary")}</Typography>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
              <DonutChart
                data={donutDataNew.filter(d => Math.abs(d.value) > 0)}
                size={240}
                strokeWidth={24}
                centerContent={(
                  <Box sx={{ textAlign: 'center', cursor: 'pointer', pointerEvents: 'auto' }} onClick={() => {
                    console.log("--> TOTAL CLICKED! Current unit before toggle:", unit);
                    setUnit(u => u === 'kg' ? 't' : 'kg');
                  }}>
                    <Typography variant="caption" sx={{ color: tok.text.muted, display: 'block', fontWeight: 500 }}>TOTAL</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: tok.text.primary }}>{totalPortfolio.toLocaleString(undefined, { maximumFractionDigits: unit === 't' ? 3 : 0 })}</Typography>
                    <Typography variant="caption" sx={{ color: tok.text.muted, fontWeight: 500, userSelect: 'none' }}>{unit} CO₂e</Typography>
                  </Box>
                )}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              {donutDataNew.filter(d => ["Scope 1", "Scope 2", "Scope 3"].includes(d.label)).map((d, i) => Math.abs(d.value) > 0 ? (
                <Box key={i} sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: d.color }} />
                    <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>{d.label}</Typography>
                  </Box>
                  <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>{Math.abs(d.value).toLocaleString(undefined, { maximumFractionDigits: unit === 't' ? 3 : 0 })} {unit} ({totalPortfolio > 0 ? ((Math.abs(d.value) / totalPortfolio) * 100).toFixed(1) : 0}%)</Typography>
                </Box>
              ) : null)}

              {donutDataNew.some(d => ["Avoided", "Biogenic"].includes(d.label) && Math.abs(d.value) > 0) && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${tok.border.divider}` }}>
                  {donutDataNew.filter(d => ["Avoided", "Biogenic"].includes(d.label)).map((d, i) => Math.abs(d.value) > 0 ? (
                    <Box key={i} sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: d.color }} />
                        <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>{d.label} Carbon</Typography>
                      </Box>
                      <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>{Math.abs(d.value).toLocaleString(undefined, { maximumFractionDigits: unit === 't' ? 3 : 0 })} {unit} ({totalPortfolio > 0 ? ((Math.abs(d.value) / totalPortfolio) * 100).toFixed(1) : 0}%)</Typography>
                    </Box>
                  ) : null)}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Bar Chart Section */}
        <Box sx={{ flex: "1 1 70%", minWidth: 450, order: { xs: 1, md: 1 } }}>
          <Box sx={{ p: ORG_PALETTE.spacing.chartPad, height: "100%", borderRadius: ORG_PALETTE.radius.card, bgcolor: tok.bg.card, border: `1px solid ${tok.border.card}` }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 4, borderBottom: `1px solid ${tok.border.divider}`, pb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle }}>{t("Emissions Breakdown")}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <FormControl variant="standard">
                  <Select
                    value={selectedGraph}
                    onChange={handleGraphChange}
                    disableUnderline
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#64748b",
                      "& .MuiSelect-select": { pb: "4px", borderBottom: `2px solid ${tok.brand?.primary || "#659d33"}`, paddingRight: "24px !important" }
                    }}
                  >
                    {graphs.map((graph) => (
                      <MenuItem key={graph.value} value={graph.value}>
                        {t(graph.label)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="standard">
                  <Select
                    value={selectedScope}
                    onChange={handleLCAStageChange}
                    disableUnderline
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#64748b",
                      "& .MuiSelect-select": { pb: "4px", borderBottom: `2px solid ${tok.brand?.primary || "#659d33"}`, paddingRight: "24px !important" }
                    }}
                  >
                    {scopes.map((scope) => (
                      <MenuItem key={scope.value} value={scope.value}>
                        {t(scope.label)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* ── End Dropdown: Date & Period Filter ── */}
                {!hasDateData ? (
                  <Chip
                    size="sm"
                    tone="neutral"
                    variant="outlined"
                    tooltip={t("Legacy case studies or records without billing periods have no date metadata")}
                  >
                    {t("No date data available")}
                  </Chip>
                ) : (
                  <FormControl variant="standard">
                    <Select
                      value={selectedPeriodKey}
                      onChange={handlePeriodKeyChange}
                      renderValue={(selected) => {
                        if (selected === "all") return t("All Periods");
                        if (selected.startsWith("annual")) {
                          const parts = selected.split(":");
                          return parts[1] ? `${t("Annual")} (${parts[1]})` : t("Annual");
                        }
                        if (selected.startsWith("quarter:")) {
                          return selected.split(":")[1];
                        }
                        if (selected.startsWith("month:")) {
                          return t(selected.split(":")[1]);
                        }
                        return t("All Periods");
                      }}
                      disableUnderline
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#64748b",
                        "& .MuiSelect-select": { pb: "4px", borderBottom: `2px solid ${tok.brand?.primary || "#659d33"}`, paddingRight: "24px !important" }
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 340,
                            borderRadius: "10px",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                          }
                        }
                      }}
                    >
                      <MenuItem value="all">{t("All Periods")}</MenuItem>

                      {availableYears.length > 0 ? (
                        availableYears.map(y => (
                          <MenuItem key={`annual:${y}`} value={`annual:${y}`}>
                            {t("Annual")} ({y})
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="annual">{t("Annual")}</MenuItem>
                      )}

                      <ListSubheader sx={{ fontWeight: 600, color: tok.text.muted, fontSize: "11px", lineHeight: "26px", pointerEvents: "none" }}>
                        {t("Quarters")}
                      </ListSubheader>
                      {availableQuarters.map(q => (
                        <MenuItem key={`quarter:${q}`} value={`quarter:${q}`}>
                          {q}
                        </MenuItem>
                      ))}

                      <ListSubheader sx={{ fontWeight: 600, color: tok.text.muted, fontSize: "11px", lineHeight: "26px", pointerEvents: "none" }}>
                        {t("Months")}
                      </ListSubheader>
                      {availableMonths.map(m => (
                        <MenuItem key={`month:${m}`} value={`month:${m}`}>
                          {t(m)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </Box>

            <Box ref={footprintBarChartRef} sx={{ width: "100%", position: "relative" }}>
              {selectedGraph === emissionGraph.value && (
                <Box sx={{ width: "100%", height: 420 }}>
                  <Bar
                    key={`emission-bar-${selectedScope}`}
                    plugins={[ChartDataLabels]}
                    data={emissionBarData}
                    options={{
                      ...barOptions,
                      indexAxis: 'y',
                      maintainAspectRatio: false,
                      layout: {
                        ...barOptions.layout,
                        padding: { right: 80 }
                      },
                      plugins: {
                        ...barOptions.plugins,
                        datalabels: {
                          display: true,
                          anchor: 'end',
                          align: 'end',
                          clip: false,
                          offset: 4,
                          formatter: (val) => {
                            if (val === undefined || val === null || val <= 0) return "";
                            return Number(val).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                          },
                          font: { family: "Poppins, sans-serif", size: 11, weight: 600 },
                          color: tok.text.primary
                        }
                      },
                      scales: {
                        ...barOptions.scales,
                        x: {
                          ...barOptions.scales?.x,
                          title: { ...(barOptions.scales?.x?.title || {}), text: `GWP [${unit} CO₂e]` },
                          ticks: { ...(barOptions.scales?.x?.ticks || {}), font: { family: GRAPH_PALETTE.font.family, size: parseInt(GRAPH_PALETTE.font.sizes.standard) || 12 }, color: tok.text.secondary },
                          grid: { display: false, drawBorder: true, drawTicks: true, borderColor: isDark ? "rgba(255,255,255, 0.4)" : "rgba(0,0,0, 0.4)" },
                          border: { display: true, width: 2, color: isDark ? "rgba(255,255,255, 0.4)" : "rgba(0,0,0, 0.4)" }
                        },
                        y: {
                          ...barOptions.scales?.y,
                          ticks: { ...(barOptions.scales?.y?.ticks || {}), font: { family: GRAPH_PALETTE.font.family, size: parseInt(GRAPH_PALETTE.font.sizes.standard) || 12 }, color: tok.text.primary },
                          grid: { display: false }
                        }
                      }
                    }}
                  />
                </Box>
              )}
              {selectedGraph === detailsGraph.value && (
                <Box sx={{ width: "100%", height: 350 }}>
                  <Bar
                    key={`details-bar-${selectedScope}`}
                    plugins={[ChartDataLabels]}
                    data={emissionDetailsBarData}
                    options={{
                      ...barOptions,
                      indexAxis: 'y',
                      maintainAspectRatio: false,
                      layout: {
                        ...barOptions.layout,
                        padding: { right: 80 }
                      },
                      plugins: {
                        ...barOptions.plugins,
                        datalabels: {
                          display: true,
                          anchor: 'end',
                          align: 'end',
                          clip: false,
                          offset: 4,
                          formatter: (val) => {
                            if (val === undefined || val === null || val <= 0) return "";
                            return Number(val).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                          },
                          font: { family: "Poppins, sans-serif", size: 11, weight: 600 },
                          color: tok.text.primary
                        }
                      },
                      scales: {
                        ...barOptions.scales,
                        x: {
                          ...barOptions.scales?.x,
                          title: { ...(barOptions.scales?.x?.title || {}), text: `GWP [${unit} CO₂e]` },
                          ticks: { ...(barOptions.scales?.x?.ticks || {}), font: { family: GRAPH_PALETTE.font.family, size: parseInt(GRAPH_PALETTE.font.sizes.standard) || 12 }, color: tok.text.secondary },
                          grid: { display: false, drawBorder: true, drawTicks: true, borderColor: isDark ? "rgba(255,255,255, 0.4)" : "rgba(0,0,0, 0.4)" },
                          border: { display: true, width: 2, color: isDark ? "rgba(255,255,255, 0.4)" : "rgba(0,0,0, 0.4)" }
                        },
                        y: {
                          ...barOptions.scales?.y,
                          ticks: { ...(barOptions.scales?.y?.ticks || {}), font: { family: GRAPH_PALETTE.font.family, size: parseInt(GRAPH_PALETTE.font.sizes.standard) || 12 }, color: tok.text.primary },
                          grid: { display: false }
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Detailed Scope Summary Table */}
      <Box sx={{ animation: "org-fadeInUp 0.5s ease" }}>
        <Paper variant="Table Container" sx={{ overflow: "hidden", border: "1px solid", borderColor: tok.border.card, borderRadius: ORG_PALETTE.radius.card, bgcolor: tok.bg.card }}>
          <TableHeaderContainer>
            <TableTitleContainer>
              <Typography variant="Table Title">
                {t("Detailed Scope Summary")}
              </Typography>
            </TableTitleContainer>
          </TableHeaderContainer>
          <ScopeSummaryTable
            rowData={tableData}
            grossTotal={grossTotal}
            isDark={isDark}
            unit={unit}
          />
        </Paper>
      </Box>
    </Box>
  );
}

export default ScopeFootprint;
